import React, { useEffect, useMemo, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import route from 'packages/novous_web/src/assets/icons/route.svg';
import clock from 'packages/novous_web/src/assets/icons/Clock.svg';
import deviation from 'packages/novous_web/src/assets/icons/Deviation.svg';
import check from 'packages/novous_web/src/assets/icons/check.svg';
import infoIcon from 'packages/novous_web/src/assets/icons/i.svg';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { IconButton } from '@mui/material';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import PermissionGuard from 'packages/novous_web/src/common/components/PermissionGuard';
import { PermissionSubcategory } from 'packages/novous_web/src/utils/permissionConstants';
import Tooltip from '@mui/material/Tooltip';

type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};
const VehiclesDataTiles: React.FC<{ data?: ColumnObjType; filterData?: any }> = ({ data, filterData }) => {
  const { user } = useAuth();
  const [countData, setCountData] = useState<any>();
  const {
    data: vehicleCountData,
    error: vehicleCountError,
    loading: vehicleCountLoading,
    fetchData: fetchVehicleCountData,
  } = useAxios<any>(`${API_ENDPOINTS.directories.VEHICLE_TILES}`, 'POST');
  // useEffect(() => {
  //   fetchVehicleCountData({ fleetId: user?.customer_id });
  // }, [user]);

  useEffect(() => {
    setCountData(vehicleCountData);
  }, [vehicleCountData]);

  // Keep a ref of last payload string to prevent duplicate API hits when the constructed
  // payload hasn't actually changed (helps with navigation + mount effects triggering twice).
  const lastPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    // Build payload defensively to avoid accidentally mapping `group` into `vehicleStatus`.
    // Also avoid duplicate calls by comparing with the last sent payload.
    const payload: any = {
      fleetId: user?.customer_id,
      lastEvaluatedKey: null,
    };

    if (filterData?.isfiltered && filterData?.data) {
      const fd = filterData.data;
      if (fd.vehicle) payload.vehicleId = fd.vehicle;
      // Prefer an explicit vehicleGroup field, fall back to group if present (they're used in different places)
      if (fd.vehicleGroup) payload.vehicleGroup = fd.vehicleGroup;
      else if (fd.group) payload.vehicleGroup = fd.group;

      if (fd.vehicleType) payload.vehicleType = fd.vehicleType;

      // Only set vehicleStatus when an explicit vehicleStatus is provided in filters.
      if (fd.vehicleStatus) payload.vehicleStatus = fd.vehicleStatus;
    }

    const last = lastPayloadRef.current;
    const next = JSON.stringify(payload);
    if (next !== last) {
      lastPayloadRef.current = next;
      fetchVehicleCountData(payload);
    }
  }, [filterData]);

  let vehicleTilesData = [
    {
      icon: route,
      heading: 'Total Vehicles',
      value: countData?.data?.totalVehicles,
      description: 'All registered vehicles in the OmniFleet system.',
      permission: {
        // category: PermissionSubcategory.VEHICLES,
        subcategory: PermissionSubcategory.TOTAL_VEHICLES,
      },
    },
    {
      icon: clock,
      heading: 'Active Vehicles',
      value: countData?.data?.activeVehicles,
      description: 'Vehicles currently in operation or transmitting data.',
      permission: {
        // category: PermissionSubcategory.VEHICLES,
        subcategory: PermissionSubcategory.ACTIVE_VEHICLES,
      },
    },
    {
      icon: deviation,
      heading: 'Available Vehicles',
      value: countData?.data?.availableVehicles,
      description: 'Vehicles that are idle and ready to be assigned.',
      permission: {
        // category: PermissionSubcategory.VEHICLES,
        subcategory: PermissionSubcategory.AVAILABLE_VEHICLES,
      },
    },
    {
      icon: check,
      heading: 'Maintenance Vehicles',
      value: countData?.data?.maintenanceVehicles,
      description: 'Vehicles under maintenance or temporarily inactive.',
      permission: {
        // category: PermissionSubcategory.VEHICLES,
        subcategory: PermissionSubcategory.MAINTENANCE_VEHICLES,
      },
    },
  ];
  const filteredTileData = useMemo(() => {
    return vehicleTilesData.filter((item, index) => {
      const stateName = 'Stat' + (index + 1);
      if (data) {
        if (data[stateName].name === item.heading) {
          return data[stateName].status;
        }
        return false;
      } else {
        return true;
      }
    });
  }, [data, vehicleTilesData]);
  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {filteredTileData.map((tile, index) => (
        <PermissionGuard
          key={index}
          category={tile.permission.subcategory}
          subcategory={tile.permission.subcategory}
        >
          <Paper
            sx={{ borderRadius: '12px' }}
            className="border border-[#f0eff8] p-4 flex flex-col gap-1 items-start"
          >
            <div className="flex items-center gap-2 w-full">
              <IconButton
                sx={(theme) => ({
                  background: theme.palette.selected,
                  padding: '4px',
                })}
              >
                <img src={tile?.icon} alt="icon" color="primary" />
              </IconButton>
              <div className="flex flex-1 min-w-0 flex-col justify-start gap-1">
                <Typography
                  variant="body2"
                  noWrap // Ensures text doesn't wrap and remains in a single line
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%', // Ensures it adapts to available space
                  }}
                >
                  {tile.heading}
                </Typography>
              </div>
              <Tooltip title={tile.description}>
                <img src={infoIcon} alt="info icon" title="More info" className="w-4 h-4 cursor-pointer" />
              </Tooltip>
            </div>
            <div className="flex items-center w-full gap-2 flex-nowrap">
              <div className="invisible w-6 h-6">{tile.icon}</div>
              <Typography
                variant="h6"
                fontWeight={600}
                className="flex-grow px-2 w-full whitespace-nowrap overflow-hidden text-ellipsis"
              >
                {tile.value}
              </Typography>
            </div>
          </Paper>
        </PermissionGuard>
      ))}
    </div>
  );
};

export default VehiclesDataTiles;
